import React, { useRef, useState } from "react";
import { FilePenLine, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { useApp } from "../../context/AppContext";

const METHODS = ["Check 支票", "Wire 电汇", "Zelle", "Cash 现金", "Credit Card 信用卡", "Other 其他"];
const inputClass = "mt-1 w-full min-h-11 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60";
const amountText = (value) => Number(value || 0).toFixed(2);
const initialReceipt = (payment) => ({
  date: String(payment.paidAt || "").slice(0, 10), method: payment.method || "",
  reference: payment.reference || "", paidAmount: amountText(payment.paidAmount ?? payment.receivedAmount ?? payment.amount),
  payee: payment.payee || "", payerName: payment.payerName || "", note: payment.payNote || "",
});
const EDITABLE = ["date", "method", "reference", "paidAmount", "payee", "payerName", "note"];

/** 纠正扫描录入的收款信息时同步更新实收统计，并通过原值比较防止覆盖他人更正。 */
export function ReceiptMetadataDialog({ api, pid, payment, stageLabel, zh, onClose, onSaved, onConflict }) {
  const { cashMode } = useApp();
  const [original] = useState(() => initialReceipt(payment));
  const [form, setForm] = useState(original);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const saving = useRef(false);
  const availableMethods = cashMode?.enabled === false ? METHODS.filter((value) => !/cash/i.test(value)) : METHODS;
  const methods = original.method && !availableMethods.includes(original.method) ? [original.method, ...availableMethods] : availableMethods;
  const changed = EDITABLE.some((key) => key === "paidAmount"
    ? amountText(form[key]) !== amountText(original[key])
    : String(form[key] || "") !== String(original[key] || ""));
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    if (saving.current || !changed || conflict) return;
    if (!form.date || !form.method || form.paidAmount === "" || !Number.isFinite(Number(form.paidAmount)) || Number(form.paidAmount) < 0) {
      setError(zh ? "请填写有效的收款日期、付款方式和实收金额" : "Enter a valid receipt date, payment method and received amount.");
      return;
    }
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      const payload = { ...form };
      EDITABLE.forEach((key) => { payload[`expected${key[0].toUpperCase()}${key.slice(1)}`] = original[key]; });
      await api.patch(`/finance/project/${pid}/payments/${encodeURIComponent(payment.stage)}/receipt`, payload);
      toast.success(zh ? "收款信息已更正，实收统计已同步" : "Receipt details corrected and collected totals updated");
      onSaved();
    } catch (e) {
      if (e?.response?.status === 409) {
        setConflict(true);
        setError(zh ? "这笔收款已被更新，请关闭后重新打开，核对最新信息。" : "This receipt has changed. Close and reopen to review the latest details.");
        onConflict?.();
      } else {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : (e?.message || (zh ? "保存失败，请重试" : "Unable to save. Please retry.")));
      }
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const textField = (key, labelZh, labelEn, testId, props = {}) => <label key={key} className="block text-xs font-medium text-slate-600">
    {zh ? labelZh : labelEn}
    <input value={form[key]} onChange={(e) => set(key, e.target.value)} disabled={busy || conflict} className={inputClass} data-testid={testId} {...props} />
  </label>;

  return <Dialog open onOpenChange={(open) => { if (!open && !saving.current) onClose(); }}>
    <DialogContent className="max-w-xl" closeLabel={zh ? "关闭" : "Close"} closeDisabled={busy}
      data-testid="receipt-metadata-dialog"
      onEscapeKeyDown={(e) => { if (saving.current) e.preventDefault(); }}
      onInteractOutside={(e) => { if (saving.current) e.preventDefault(); }}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><FilePenLine className="h-5 w-5 shrink-0 text-brand-600" />{zh ? "编辑收款信息" : "Edit receipt details"}</DialogTitle>
        <DialogDescription>{stageLabel}</DialogDescription>
      </DialogHeader>
      <form onSubmit={save} className="space-y-4">
        <p className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500">{zh ? "可更正识别后填入的公司、付款方、凭证编号、日期、实收金额、付款方式和备注。实收金额会同步财务统计；计划金额与“已收”状态保持不变，所有更正留有记录。" : "Correct the recognized company, payer, reference, date, received amount, payment method and note. Received amount updates finance totals; planned amount and paid status stay unchanged, with an audit trail."}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {textField("payee", "公司 / 收款方", "Company / payee", "receipt-edit-payee", { autoComplete: "organization" })}
          {textField("payerName", "付款方", "Payer", "receipt-edit-payer", { autoComplete: "name" })}
          {textField("reference", "凭证编号 / 支票号", "Receipt / check reference", "receipt-edit-reference")}
          {textField("paidAmount", "实收金额 ($) *", "Amount received ($) *", "receipt-edit-amount", { type: "number", min: "0", step: "0.01", required: true, inputMode: "decimal" })}
          {textField("date", "收款日期 *", "Receipt date *", "receipt-edit-date", { type: "date", required: true, autoFocus: true })}
          <label className="block text-xs font-medium text-slate-600">{zh ? "付款方式 *" : "Payment method *"}
            <select required value={form.method} onChange={(e) => set("method", e.target.value)} disabled={busy || conflict} className={inputClass} data-testid="receipt-edit-method">
              {!original.method && <option value="">{zh ? "请选择" : "Select a method"}</option>}
              {methods.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label className="block text-xs font-medium text-slate-600 sm:col-span-2">{zh ? "备注 / 付款说明" : "Note / payment details"}
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)} disabled={busy || conflict} rows={3} maxLength={200} className={`${inputClass} min-h-20`} data-testid="receipt-edit-note" />
          </label>
        </div>
        {error && <p role="alert" className="text-sm text-red-600" data-testid="receipt-edit-error">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="receipt-edit-cancel">{zh ? "取消" : "Cancel"}</Button>
          <Button type="submit" disabled={busy || conflict || !changed || !form.date || !form.method || form.paidAmount === ""} data-testid="receipt-edit-save">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{busy ? (zh ? "保存中…" : "Saving…") : (zh ? "保存更正" : "Save changes")}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
}
